import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ErrorResult, ValidationError } from '../../models/error.model';

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {

  constructor() { }

  /**
   * Main error handling method that processes HTTP errors and returns structured error data
   * @param error - The HTTP error response
   * @param formGroup - Optional form group for field-level validation errors
   * @returns ErrorResult with message, validation errors, and status code
   */
  handleError(error: any, formGroup?: FormGroup): ErrorResult {
    console.error('API Error:', error);
    
    let message = this.extractErrorMessage(error);
    const validationErrors = this.extractValidationErrors(error);
    const statusCode = error?.status || error?.statusCode || 500;

    // If we have validation errors and a form group, apply them to form controls
    if (validationErrors.length > 0 && formGroup) {
      this.applyValidationErrorsToForm(validationErrors, formGroup);
    }

    // For 400 with validation errors, prefer showing validation messages instead of generic "Bad Request"
    if (statusCode === 400 && validationErrors.length > 0) {
      const validationMessage = validationErrors.map(e => e.message).join(' ');
      if (validationMessage && (/bad request|please check your input/i.test(message) || message.length < 30)) {
        message = validationMessage;
      }
    }

    return {
      message,
      validationErrors,
      statusCode
    };
  }

  /**
   * Extract a meaningful error message from the HTTP error response
   * @param error - The HTTP error response
   * @returns A user-friendly error message
   */
  extractErrorMessage(error: any): string {
    // Handle different error response structures
    if (error?.error?.message) {
      // If message is an array (NestJS validation), join only string elements
      if (Array.isArray(error.error.message)) {
        const parts = error.error.message.filter((m: unknown) => typeof m === 'string' && m.trim());
        if (parts.length > 0) {
          return parts.join(' ');
        }
      }
      if (typeof error.error.message === 'string') {
        return error.error.message;
      }
    }
    
    if (error?.error?.error) {
      return error.error.error;
    }
    
    if (error?.message) {
      return error.message;
    }
    
    if (error?.error) {
      // Handle validation error objects
      if (typeof error.error === 'object') {
        const firstError = Object.values(error.error)[0];
        if (Array.isArray(firstError)) {
          return firstError[0];
        }
        if (typeof firstError === 'string') {
          return firstError;
        }
      }
      return String(error.error);
    }

    // Fallback based on status code
    switch (error?.status) {
      case 400:
        return 'Bad Request - Please check your input';
      case 401:
        return 'Unauthorized - Please login again';
      case 403:
        return 'Access Denied - You do not have permission';
      case 404:
        return 'Resource not found';
      case 409:
        return 'Conflict - Resource already exists';
      case 422:
        return 'Validation Error - Please check your input';
      case 500:
        return 'Server Error - Please try again later';
      default:
        return 'An unexpected error occurred';
    }
  }

  /**
   * Extract validation errors from the HTTP error response
   * @param error - The HTTP error response
   * @returns Array of ValidationError objects
   */
  extractValidationErrors(error: any): ValidationError[] {
    const validationErrors: ValidationError[] = [];

    if (error?.error?.errors) {
      // Laravel validation errors format
      const errors = error.error.errors;
      Object.keys(errors).forEach(field => {
        const messages = errors[field];
        if (Array.isArray(messages)) {
          messages.forEach(message => {
            validationErrors.push({
              field: this.mapBackendFieldToFormControl(field),
              message: message
            });
          });
        } else if (typeof messages === 'string') {
          validationErrors.push({
            field: this.mapBackendFieldToFormControl(field),
            message: messages
          });
        }
      });
    } else if (error?.error?.message && Array.isArray(error.error.message)) {
      // NestJS validation errors format: { message: ["field must be valid"], error: "Bad Request" }
      error.error.message.forEach((message: string) => {
        // Extract field name from message (e.g., "contactEmail must be an email" -> "contactEmail")
        const fieldMatch = message.match(/^(\w+)\s/);
        const field = fieldMatch ? fieldMatch[1] : 'general';
        validationErrors.push({
          field: this.mapBackendFieldToFormControl(field),
          message: message
        });
      });
    } else if (error?.error?.message && typeof error.error.message === 'string') {
      // Single message format: { message: "Module with this slug already exists", error: "Conflict" }
      const message = error.error.message;
      let field = 'general';
      
      // Extract field name from various message patterns
      if (message.toLowerCase().includes('current password')) {
        field = 'currentPassword';
      } else if (message.toLowerCase().includes('new password')) {
        field = 'newPassword';
      } else {
        // Try to extract field name from message (e.g., "Module with this slug already exists" -> "slug")
        const fieldMatch = message.match(/(?:with this )?(\w+)(?: already exists| must be| is required)/i);
        field = fieldMatch ? fieldMatch[1] : 'general';
      }
      
      validationErrors.push({
        field: this.mapBackendFieldToFormControl(field),
        message: message
      });
    } else if (error?.error && typeof error.error === 'object') {
      // Generic object validation errors
      Object.keys(error.error).forEach(field => {
        const value = error.error[field];
        if (typeof value === 'string') {
          validationErrors.push({
            field: this.mapBackendFieldToFormControl(field),
            message: value
          });
        } else if (Array.isArray(value)) {
          value.forEach(message => {
            validationErrors.push({
              field: this.mapBackendFieldToFormControl(field),
              message: message
            });
          });
        }
      });
    }

    return validationErrors;
  }

  /**
   * Map backend field names to frontend form control names
   * @param backendField - The field name from backend
   * @returns The corresponding form control name
   */
  mapBackendFieldToFormControl(backendField: string): string {
    const fieldMappings: { [key: string]: string } = {
      'tenantId': 'tenantId',
      'name': 'name',
      'slug': 'slug',
      'description': 'description',
      'logo': 'logo',
      'website': 'website',
      'contactEmail': 'contactEmail',
      'contactPhone': 'contactPhone',
      'address': 'address',
      'city': 'city',
      'country': 'country',
      'isActive': 'isActive',
      'settings': 'settings',
      'subscription': 'subscription',
      'moduleId': 'moduleId',
      'configuration': 'configuration',
      'permissions': 'permissions',
      'email': 'email',
      'password': 'password',
      'firstName': 'firstName',
      'lastName': 'lastName',
      'phone': 'phone',
      'role': 'role',
      'lastLoginAt': 'lastLoginAt',
      'version': 'version',
      'status': 'status',
      'features': 'features',
      'sortOrder': 'sortOrder',
      'isCore': 'isCore',
      'type': 'type',
      'activatedAt': 'activatedAt',
      'deactivatedAt': 'deactivatedAt'
    };

    return fieldMappings[backendField] || backendField;
  }

  /**
   * Apply validation errors to form controls
   * @param validationErrors - Array of validation errors
   * @param formGroup - The form group to apply errors to
   */
  private applyValidationErrorsToForm(validationErrors: ValidationError[], formGroup: FormGroup): void {
    validationErrors.forEach(validationError => {
      const control = formGroup.get(validationError.field);
      if (control) {
        control.setErrors({ 
          serverError: validationError.message 
        });
        control.markAsTouched();
      }
    });
  }

  /**
   * Get unmapped validation errors (errors that couldn't be applied to form controls)
   * @param validationErrors - Array of validation errors
   * @param formGroup - The form group
   * @returns Array of unmapped validation errors
   */
  getUnmappedValidationErrors(validationErrors: ValidationError[], formGroup: FormGroup): ValidationError[] {
    return validationErrors.filter(error => {
      const control = formGroup.get(error.field);
      return !control;
    });
  }
}
